import React, { useState, useEffect, useMemo, useCallback } from 'react';
import useFetch from '../../services/useFetch.js'
import { DataGrid } from "@mui/x-data-grid"
import { dataGridSx } from '../ui/dataGridStyles.js';
import { withResizableColumns, useColumnWidths } from '../ui/ResizableGridHeader.jsx';
import { currencyFormatter } from '../Helpers/CurrencyFormat.js';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Drawer } from '@mui/material';
import ModifierDrawer from './ModifierDrawer.jsx';
import MetricCard from '../ui/MetricCard.jsx';

const DEFAULT_WIDTHS = {
    iName: 200,
    variant: 140,
    units: 120,
    amtSld: 130,
    cogs: 120,
    profit: 120,
    date: 110,
    seeModifiers: 200,
};

function summarizeOrders(lines) {
    if (!Array.isArray(lines) || lines.length === 0) {
        return {
            totalRevenue: 0,
            totalCost: 0,
            topItem: null,
        };
    }

    let totalRevenue = 0;
    let totalCost = 0;
    const byProduct = new Map();

    for (const line of lines) {
        const revenue = typeof line.amountSold === 'number' ? line.amountSold : 0;
        const cost = typeof line.totalCOGS === 'number' ? line.totalCOGS : 0;
        const units = Number(line.totalUnits) || 0;
        totalRevenue += revenue;
        totalCost += cost;

        const key = `${line.itemName || 'Unknown'}::${line.itemVariant || 'None'}`;
        const existing = byProduct.get(key) || {
            name: line.itemName || 'Unknown',
            variant: line.itemVariant && line.itemVariant !== 'None' ? line.itemVariant : null,
            revenue: 0,
            units: 0,
        };
        existing.revenue += revenue;
        existing.units += units;
        byProduct.set(key, existing);
    }

    let topItem = null;
    for (const item of byProduct.values()) {
        if (!topItem || item.revenue > topItem.revenue || (item.revenue === topItem.revenue && item.units > topItem.units)) {
            topItem = item;
        }
    }

    return { totalRevenue, totalCost, topItem };
}

export default function OrdersTable({ startDate, endDate, reload }) {

    const [row, setRow] = useState([])
    const [openModifierDrawer, setOpenModifierDrawer] = useState(false)
    const [modifierDrawerData, setModifierDrawerData] = useState(null)

    const { data, error, load } = useFetch(`api/orders-log?startDateStr=${startDate}&endDateStr=${endDate}`, reload)
    const [menuItemData, setMenuItemData] = useState(null)
    const { widths, onWidthChange } = useColumnWidths(DEFAULT_WIDTHS)

    useEffect(() => {
        if (data === null) {
            return
        } else {
            const newMenuItems = data.flatMap(e => e.menuItems);
            setMenuItemData(newMenuItems)
        }

    }, [data])
    useEffect(() => {
        if (menuItemData) {
            setRow(menuItemData.map((arr, ind) => ({
                id: ind,
                iName: arr.itemName,
                variant: arr.itemVariant ? arr.itemVariant : 'None',
                units: arr.totalUnits,
                amtSld: typeof arr.amountSold === 'number' ? arr.amountSold : 'None',
                cogs: typeof arr.totalCOGS === 'number' ? arr.totalCOGS : 'None',
                profit: typeof arr.totalItemProfit === 'number' ? arr.totalItemProfit : 'None',
                date: arr.orderDate,
            })))
        }
    }, [menuItemData])

    const stats = useMemo(() => summarizeOrders(menuItemData), [menuItemData])
    const statsLoading = load || menuItemData === null

    const modifierDetails = useCallback((orderRow) => {
        const dataIndex = menuItemData?.[orderRow.id]
        setOpenModifierDrawer(true)
        setModifierDrawerData(dataIndex?.modifiersOrdered)
    }, [menuItemData])

    const baseColumns = useMemo(() => ([
        { field: 'iName', headerName: 'Product', minWidth: 120 },
        { field: 'variant', headerName: 'Variation', minWidth: 100 },
        { field: 'units', headerName: 'Total Quantity', minWidth: 90 },
        {
            field: 'amtSld',
            headerName: 'Total Revenue',
            minWidth: 100,
            valueFormatter: ({ value }) => (value !== null ? currencyFormatter(value, 2) : 'None'),
        },
        {
            field: 'cogs',
            headerName: 'Total Cost',
            minWidth: 100,
            valueFormatter: ({ value }) => (value !== null ? currencyFormatter(value, 2) : 'None'),
        },
        {
            field: 'profit',
            headerName: 'Unit Margin',
            minWidth: 90,
            valueFormatter: ({ value }) => (value !== null ? currencyFormatter(value, 2) : 'None'),
        },
        {
            field: 'date',
            headerName: 'Date',
            minWidth: 90,
            valueFormatter: ({ value }) => new Date(value).toLocaleDateString(),
        },
        {
            field: 'seeModifiers',
            headerName: 'Modifiers',
            minWidth: 160,
            sortable: false,
            renderCell: (params) => {
                if (menuItemData?.[params.id]?.modifiersOrdered?.length > 0) {
                    return (
                        <button
                            type="button"
                            style={{ height: '25px', background: 'rgba(91, 127, 94, 0.18)', border: "none", color: '#2C241B', borderRadius: '5px', width: '100%', maxWidth: 220, fontWeight: '600' }}
                            onClick={() => modifierDetails(params)}
                        >
                            See Modifiers Ordered <KeyboardArrowDownIcon fontSize='small' />
                        </button>
                    );
                }
                return <p style={{ padding: '15px 15px', margin: '0', color: "gray" }}>No Modifiers Ordered</p>;
            },
        },
    ]), [menuItemData, modifierDetails]);

    const columns = useMemo(
        () => withResizableColumns(baseColumns, widths, onWidthChange),
        [baseColumns, widths, onWidthChange],
    );

    const toggleDrawer = (newOpen) => () => {
        setOpenModifierDrawer(newOpen);
    };

    const topLabel = stats.topItem
        ? (stats.topItem.variant
            ? `${stats.topItem.name} — ${stats.topItem.variant}`
            : stats.topItem.name)
        : '—';

    return (
        <div>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: 2,
                    mb: 2.5,
                }}
            >
                <MetricCard
                    label="Total revenue"
                    loading={statsLoading}
                    value={currencyFormatter(stats.totalRevenue, 2)}
                    detail={`${menuItemData?.length || 0} line${(menuItemData?.length || 0) === 1 ? '' : 's'} in range`}
                />
                <MetricCard
                    label="Total cost"
                    loading={statsLoading}
                    value={currencyFormatter(stats.totalCost, 2)}
                    detail={`Margin ${currencyFormatter(stats.totalRevenue - stats.totalCost, 2)}`}
                />
                <MetricCard
                    label="Top item"
                    loading={statsLoading}
                    value={topLabel}
                    detail={
                        stats.topItem
                            ? `${currencyFormatter(stats.topItem.revenue, 2)} · ${stats.topItem.units} sold`
                            : 'No sales in this range'
                    }
                />
            </Box>

            <div className="row " style={{ width: "100%", position: 'relative' }}>
                <div className="col-12 menu-list" style={{ height: '70vh' }}>
                    <DataGrid
                        rows={row}
                        columns={columns}
                        rowHeight={35}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 25,
                                },
                            },
                            sorting: {
                                sortModel: [{ field: 'amtSld', sort: 'desc' }],
                            },
                        }}
                        sx={{
                            ...dataGridSx,
                            border: 'none',
                            height: '100%',
                            '& .MuiDataGrid-columnHeader': {
                                overflow: 'visible',
                            },
                            '& .MuiDataGrid-columnHeaderTitleContainer': {
                                overflow: 'visible',
                            },
                        }}
                    />
                </div>

            </div>
            <Drawer anchor={'bottom'} open={openModifierDrawer} onClose={toggleDrawer(false)} sx={{ background: 'lighgray' }}>
                <ModifierDrawer modifierData={modifierDrawerData} close={toggleDrawer(false)} />
            </Drawer>
        </div>
    );
};
